import React, { Component } from 'react';
import classNames from 'classnames';
import {
  episodePlayRequested,
  episodePlayStarted,
  episodeDownloadClick,
  episodeEnclosureError,
} from '@wix/bi-logger-podcast/v2';

import { toHttps } from '../../../../lib/utils/url';
import compose from '../../../../lib/utils/compose';
import stringifyError from '../../../../lib/utils/stringifyError';

import {
  getPlayer,
  PlayerType,
  IPlayerWrapper,
} from '../../../../components/media/mediaPlayer';

import styles from './EpisodePageEnclosure.scss';
import {
  getAudioStyles,
  getVideoStyles,
  IEnclosureStyles,
} from './EpisodePageEnclosure.styles';

import { biLogger, biTransaction } from '../../../../client/bi';
import { BiEvent } from '../../../../lib/bi/types';

import {
  IWithSettingsProps,
  withSettings,
} from '../../../../components/context/settings';
import { IWithI18nProps, withI18n } from '../../../../components/context/i18n';

import {
  IPodcastEpisode,
  IPodcastEpisodeEnclosure,
} from '@wix/podcasts-common/dist/podcast';

interface IEpisodePageEnclosureProps
  extends IWithSettingsProps,
    IWithI18nProps {
  episode: IPodcastEpisode;
}

interface IEpisodePageEnclosureState {
  enclosure?: IPodcastEpisodeEnclosure;
  player?: IPlayerWrapper;
  error?: any;
}

class EpisodePageEnclosure extends Component<
  IEpisodePageEnclosureProps,
  IEpisodePageEnclosureState
> {
  state: IEpisodePageEnclosureState = {};

  static getDerivedStateFromProps(
    props: IEpisodePageEnclosureProps,
    state: IEpisodePageEnclosureState,
  ) {
    const enclosure = props.episode.enclosure;

    if (enclosure === state.enclosure) {
      return null;
    }

    return {
      enclosure,
      player: getPlayer(enclosure.type),
      error: null,
    };
  }

  onPlayRequested = () => {
    const { episode } = this.props;
    const { player } = this.state;

    const transaction = biTransaction.start(BiEvent.EPISODE_PLAY_REQUESTED);

    biLogger.report(
      episodePlayRequested({
        episodeId: episode.id,
        type: player.type,
        correlationId: transaction.id,
      }),
    );
  };

  onPlayStarted = () => {
    const { episode } = this.props;
    const { player } = this.state;

    const transaction = biTransaction.end(BiEvent.EPISODE_PLAY_STARTED);

    biLogger.report(
      episodePlayStarted({
        episodeId: episode.id,
        type: player.type,
        correlationId: transaction && transaction.id,
      }),
    );
  };

  onDownloadClick = () => {
    const { episode } = this.props;
    const { player } = this.state;

    biLogger.report(
      episodeDownloadClick({
        episodeId: episode.id,
        url: episode.enclosure.url,
        type: player.type,
      }),
    );
  };

  onError = (error) => {
    const { episode } = this.props;

    const transaction = biTransaction.end(BiEvent.EPISODE_PLAY_ERROR);

    biLogger.report(
      episodeEnclosureError({
        enclosureUrl: episode.enclosure.url,
        errorType: stringifyError(error),
        correlationId: transaction && transaction.id,
      }),
    );

    this.setState({
      error,
    });
  };

  getClassNames(): IEnclosureStyles {
    const {
      settings: { colors, fonts },
    } = this.props;
    const { player } = this.state;
    let dynamicStyles: IEnclosureStyles;

    switch (player.type) {
      case PlayerType.AUDIO:
        dynamicStyles = getAudioStyles({ colors, fonts });
        return {
          enclosureWrapper: classNames(
            styles.enclosureWrapper,
            dynamicStyles.enclosureWrapper,
          ),
          enclosureContainer: classNames(
            styles.enclosureContainer,
            styles.enclosureContainerAudio,
            dynamicStyles.enclosureContainer,
          ),
        };

      case PlayerType.VIDEO:
        dynamicStyles = getVideoStyles({ colors, fonts });
        return {
          enclosureWrapper: classNames(
            styles.enclosureWrapper,
            dynamicStyles.enclosureWrapper,
          ),
          enclosureContainer: classNames(
            styles.enclosureContainer,
            styles.enclosureContainerVideo,
            dynamicStyles.enclosureContainer,
          ),
        };

      default:
        return { enclosureContainer: '', enclosureWrapper: '' };
    }
  }

  render() {
    const { episode, settings, t } = this.props;
    const { player, error } = this.state;

    if (!player) {
      return null;
    }

    const enclosureUrl = toHttps(episode.enclosure.url);
    const enclosureType = episode.enclosure.type;

    const Player = player.component;
    const {
      enclosureWrapper: wrapperClassName,
      enclosureContainer: containerClassName,
    } = this.getClassNames();

    return (
      <div data-hook="episode.enclosure" className={wrapperClassName}>
        <div className={containerClassName}>
          <Player
            url={enclosureUrl}
            mimeType={enclosureType}
            allowDownload={settings.common.allowDownload}
            controlsColor={settings.colors.episode.playerControlsColor}
            onPlayRequested={this.onPlayRequested}
            onPlay={this.onPlayStarted}
            onDownloadClick={this.onDownloadClick}
            onError={this.onError}
          />
          {error && (
            <div className={styles.error}>
              {t('player.error.could-not-play')}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default compose(withSettings, withI18n)(EpisodePageEnclosure);
